export interface PreparedUploadFile {
  name: string;
  type: string;
  dataUrl: string;
  /** Whether the image was downscaled before encoding. */
  resized?: boolean;
}

// Cap raw camera-photo data URLs at ~6MB after base64 inflation so the Worker
// stays well under Cloudflare's request body limits and OpenAI doesn't reject
// the payload. Anything larger gets canvas-resized to 2000px on the long
// edge before being re-encoded as JPEG (q=0.85). Non-images are passed through.
const MAX_IMAGE_DATA_URL_BYTES = 6 * 1024 * 1024;
const TARGET_LONG_EDGE_PX = 2000;
const RESIZE_OUTPUT_TYPE = 'image/jpeg';
const RESIZE_QUALITY = 0.85;

export async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Unable to read ${file.name}`));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
}

export async function prepareFilesForUpload(files: File[]): Promise<PreparedUploadFile[]> {
  const prepared = await Promise.all(
    files.map(async (file) => {
      const isImage = file.type.startsWith('image/');
      // Skip resize for non-images and small images.
      if (!isImage || file.size <= MAX_IMAGE_DATA_URL_BYTES) {
        return {
          name: file.name,
          type: file.type,
          dataUrl: await fileToDataUrl(file),
        };
      }
      try {
        const resized = await resizeImageFile(file);
        return { name: file.name, type: resized.type, dataUrl: resized.dataUrl, resized: true };
      } catch {
        // If resize fails (e.g. unsupported codec, headless browser), fall
        // back to the raw data URL — the Worker error path will surface a
        // specific message rather than us silently dropping the file.
        return {
          name: file.name,
          type: file.type,
          dataUrl: await fileToDataUrl(file),
        };
      }
    }),
  );
  return prepared;
}

export function pickImageDataUrls(files: PreparedUploadFile[], limit = 3): string[] {
  return files.filter((f) => f.type.startsWith('image/')).slice(0, limit).map((f) => f.dataUrl);
}

interface ResizedImage {
  type: string;
  dataUrl: string;
}

/**
 * Downscale an image File using a canvas so the resulting data URL fits within
 * the Worker's request budget. Long edge clamps to TARGET_LONG_EDGE_PX,
 * preserving aspect ratio. Output is always JPEG since base64-encoded PNGs
 * are 30-50% larger for typical photos.
 */
async function resizeImageFile(file: File): Promise<ResizedImage> {
  const bitmap = await loadBitmap(file);
  try {
    const { width: srcW, height: srcH } = bitmap;
    const longEdge = Math.max(srcW, srcH);
    const scale = longEdge > TARGET_LONG_EDGE_PX ? TARGET_LONG_EDGE_PX / longEdge : 1;
    const dstW = Math.max(1, Math.round(srcW * scale));
    const dstH = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(bitmap, 0, 0, dstW, dstH);
    const dataUrl = canvas.toDataURL(RESIZE_OUTPUT_TYPE, RESIZE_QUALITY);
    return { type: RESIZE_OUTPUT_TYPE, dataUrl };
  } finally {
    if ('close' in bitmap && typeof bitmap.close === 'function') {
      bitmap.close();
    }
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  // Fallback for environments without createImageBitmap. Should be rare in
  // 2026 browsers, but keeps the resize utility resilient.
  const dataUrl = await fileToDataUrl(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = dataUrl;
  });
}
